import React, { useState } from 'react';
import { Search, Filter, ListChecks, MapPin, Phone, Mail, MessageSquare, PhoneCall, Search as SearchIcon } from 'lucide-react';

export function LeadsView() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any>(null);

  // Sample leads data
  const sampleLeads = [
    {
      id: 1,
      name: 'John Smith',
      address: '123 Main St, Orlando, FL 32801',
      phone: '(407) 555-0123',
      email: 'john@email.com',
      status: 'New',
      delinquent: '$8,500',
      value: '$250,000',
      skiptraced: false,
      conversations: [
        { type: 'call', date: '2025-12-01', note: 'Left voicemail' },
        { type: 'text', date: '2025-12-01', note: 'Sent initial message' }
      ]
    },
    {
      id: 2,
      name: 'Mary Johnson',
      address: '456 Oak Ave, Tampa, FL 33601',
      phone: '(813) 555-0456',
      email: 'mary@email.com',
      status: 'Contacted',
      delinquent: '$12,000',
      value: '$320,000',
      skiptraced: true,
      conversations: [
        { type: 'call', date: '2025-11-28', note: 'Spoke with owner, interested' },
        { type: 'text', date: '2025-11-29', note: 'Follow-up message sent' }
      ]
    },
    {
      id: 3,
      name: 'Robert Davis',
      address: '789 Pine St, Miami, FL 33101',
      phone: '(305) 555-0789',
      email: 'robert@email.com',
      status: 'Analyzing',
      delinquent: '$5,400',
      value: '$180,000',
      skiptraced: true,
      conversations: []
    },
    {
      id: 4,
      name: 'Lisa Wilson',
      address: '321 Elm Dr, Jacksonville, FL 32099',
      phone: '(904) 555-0321',
      email: 'lisa@email.com',
      status: 'Offer Made',
      delinquent: '$15,200',
      value: '$425,000',
      skiptraced: true,
      conversations: [
        { type: 'call', date: '2025-11-25', note: 'Initial contact made' },
        { type: 'call', date: '2025-11-27', note: 'Discussed offer details' },
        { type: 'text', date: '2025-11-28', note: 'Sent formal offer' }
      ]
    },
    {
      id: 5,
      name: 'James Brown',
      address: '654 Maple Ln, Tallahassee, FL 32301',
      phone: '(850) 555-0654',
      email: 'james@email.com',
      status: 'New',
      delinquent: '$6,800',
      value: '$195,000',
      skiptraced: false,
      conversations: []
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'Contacted': return 'bg-tdb-accent/10 text-tdb-accent';
      case 'Analyzing': return 'bg-amber-100 text-amber-800';
      case 'Offer Made': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSkiptrace = (lead: any) => {
    console.log('Skiptracing lead:', lead);
    alert(`Skiptrace functionality for ${lead.name}\n\nThis will be connected to skiptrace API (PropStream, BatchSkipTracing, etc.)`);
  };

  const handleCall = (lead: any) => {
    console.log('Calling lead:', lead);
    alert(`Click-to-call for ${lead.name}: ${lead.phone}\n\nThis will be connected to Twilio or similar calling service.`);
  };

  const handleText = (lead: any) => {
    console.log('Texting lead:', lead);
    alert(`Send SMS to ${lead.name}: ${lead.phone}\n\nThis will be connected to Twilio SMS service.`);
  };

  const handleViewConversations = (lead: any) => {
    setSelectedLead(lead);
    setShowConversationModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-tdb-primary">Leads</h1>
          <p className="text-gray-600 mt-1">Manage and track your imported real estate leads</p>
        </div>
      </div>

      {/* Action Bar */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search leads by name, address, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent"
              />
            </div>
          </div>

          {/* Filter */}
          <button className="flex items-center justify-center space-x-2 px-4 py-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all font-semibold text-gray-700">
            <Filter className="w-5 h-5" />
            <span>Filter</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
          <div className="text-sm text-gray-600 font-medium">Total Leads</div>
          <div className="text-3xl font-bold text-tdb-primary mt-1">1,247</div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-tdb-accent">
          <div className="text-sm text-gray-600 font-medium">Skiptraced</div>
          <div className="text-3xl font-bold text-tdb-primary mt-1">892</div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-amber-500">
          <div className="text-sm text-gray-600 font-medium">Contacted</div>
          <div className="text-3xl font-bold text-tdb-primary mt-1">543</div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
          <div className="text-sm text-gray-600 font-medium">Hot Leads</div>
          <div className="text-3xl font-bold text-tdb-primary mt-1">67</div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-bold text-tdb-primary flex items-center space-x-2">
            <ListChecks className="w-5 h-5 text-tdb-accent" />
            <span>All Leads</span>
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Owner</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Property</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Tax Delinquent</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Est. Value</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sampleLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-semibold text-gray-900">{lead.name}</div>
                    {lead.skiptraced && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded">
                        Skiptraced
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-start space-x-2">
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-gray-600">{lead.address}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span>{lead.phone}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span>{lead.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-bold text-red-600">{lead.delinquent}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-bold text-green-600">{lead.value}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(lead.status)}`}>
                      {lead.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleSkiptrace(lead)}
                        disabled={lead.skiptraced}
                        className={`p-2 rounded-lg transition-all ${
                          lead.skiptraced
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                        }`}
                        title="Skiptrace"
                      >
                        <SearchIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCall(lead)}
                        className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-all"
                        title="Call"
                      >
                        <PhoneCall className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleText(lead)}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-all"
                        title="Send SMS"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleViewConversations(lead)}
                        className="p-2 bg-tdb-accent/10 text-tdb-accent rounded-lg hover:bg-tdb-accent/20 transition-all relative"
                        title="View Conversations"
                      >
                        <MessageSquare className="w-4 h-4" />
                        {lead.conversations.length > 0 && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                            {lead.conversations.length}
                          </span>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold">1-5</span> of <span className="font-semibold">1,247</span> leads
          </div>
          <div className="flex space-x-2">
            <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-100">
              Previous
            </button>
            <button className="px-4 py-2 bg-tdb-accent text-white rounded-lg text-sm font-semibold hover:bg-[#0ea5ef]">
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Conversation History Modal */}
      {showConversationModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-tdb-primary to-tdb-secondary px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold">{selectedLead.name}</h3>
                  <p className="text-sm text-gray-200 mt-1">{selectedLead.address}</p>
                </div>
                <button
                  onClick={() => setShowConversationModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-all"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <h4 className="font-bold text-tdb-primary mb-4 flex items-center space-x-2">
                <MessageSquare className="w-5 h-5 text-tdb-accent" />
                <span>Conversation History</span>
              </h4>

              {selectedLead.conversations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>No conversations yet</p>
                  <p className="text-sm mt-1">Start by calling or texting this lead</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedLead.conversations.map((conv: any, index: number) => (
                    <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {conv.type === 'call' ? (
                            <PhoneCall className="w-4 h-4 text-green-600" />
                          ) : (
                            <MessageSquare className="w-4 h-4 text-blue-600" />
                          )}
                          <span className="font-semibold text-sm capitalize">{conv.type}</span>
                        </div>
                        <span className="text-xs text-gray-500">{conv.date}</span>
                      </div>
                      <p className="text-sm text-gray-700">{conv.note}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Quick Actions */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h5 className="font-semibold text-sm text-gray-700 mb-3">Quick Actions</h5>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      handleCall(selectedLead);
                      setShowConversationModal(false);
                    }}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all font-semibold"
                  >
                    <PhoneCall className="w-5 h-5" />
                    <span>Call Now</span>
                  </button>
                  <button
                    onClick={() => {
                      handleText(selectedLead);
                      setShowConversationModal(false);
                    }}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all font-semibold"
                  >
                    <MessageSquare className="w-5 h-5" />
                    <span>Send SMS</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
